import { getReservationModeLabels } from '@iheartjane/menu';
import {
  Box,
  Flex,
  Hide,
  Link,
  Typography,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { useMemo, useState } from 'react';

import { useMenu } from '@jane/shared-ecomm/providers';
import { getOrderingSchedule } from '@jane/shared-ecomm/util';
import { getTodaysScheduleHours } from '@jane/shared/util';

import { StyledGrid } from './MenuHeader.styles';
import { MenuSchedule } from './MenuSchedule';

export const MenuHours = ({ inModal = true }: { inModal?: boolean }) => {
  const [showSchedule, setShowSchedule] = useState(false);
  const isMobile = useMobileMediaQuery({});
  const { store } = useMenu();

  const { curbsideSchedule, deliverySchedule, pickupSchedule } = useMemo(
    () =>
      getOrderingSchedule({
        store,
        type: ['pickup', 'delivery', 'curbside'],
      }),
    [store]
  );

  const { pickupLabel, deliveryLabel, curbsideLabel } =
    getReservationModeLabels(store.reservation_mode_labels);

  const pickupToday = pickupSchedule?.getToday();
  const deliveryToday = deliverySchedule?.getToday();
  const curbsideToday = curbsideSchedule?.getToday();

  const { pickupHours, deliveryHours, curbsideHours } = getTodaysScheduleHours({
    pickup: {
      opening: pickupToday?.formattedOpeningTime,
      closing: pickupToday?.formattedClosingTime,
    },
    delivery: {
      opening: deliveryToday?.formattedOpeningTime,
      closing: deliveryToday?.formattedClosingTime,
    },
    curbside: {
      opening: curbsideToday?.formattedOpeningTime,
      closing: curbsideToday?.formattedClosingTime,
    },
  });

  const hasDeliverySchedule =
    deliverySchedule && !deliverySchedule?.isAlwaysClosed();
  const hasPickupSchedule = pickupSchedule && !pickupSchedule?.isAlwaysClosed();
  const hasCurbsideSchedule =
    curbsideSchedule && !curbsideSchedule?.isAlwaysClosed();

  const fallbackLabel = store.allow_off_hours_ordering
    ? 'Pre-order for later'
    : 'Closed';

  if (!inModal) {
    return isMobile ? (
      <Flex flexWrap="wrap">
        {hasPickupSchedule && (
          <Typography whiteSpace="nowrap">
            {`Open until ${pickupToday?.formattedClosingTime || fallbackLabel}`}
          </Typography>
        )}
        {hasPickupSchedule && hasDeliverySchedule && (
          <Typography mx={4} as="span">
            &#8226;
          </Typography>
        )}
        {hasDeliverySchedule && (
          <Typography whiteSpace="nowrap">
            {deliveryLabel}{' '}
            {`until ${deliveryToday?.formattedClosingTime || fallbackLabel}`}
          </Typography>
        )}
      </Flex>
    ) : (
      <Flex flexWrap="wrap">
        {hasPickupSchedule && (
          <Typography whiteSpace="nowrap">
            {pickupLabel} {pickupHours || fallbackLabel}
          </Typography>
        )}
        {hasPickupSchedule && hasDeliverySchedule && (
          <Typography mx={4} as="span">
            &#8226;
          </Typography>
        )}
        {hasDeliverySchedule && (
          <Typography whiteSpace="nowrap">
            {deliveryLabel} {deliveryHours || fallbackLabel}
          </Typography>
        )}
      </Flex>
    );
  }

  return (
    <Box>
      <Typography variant="body-bold" pb={8}>
        Today's hours
      </Typography>
      <StyledGrid pb={8}>
        {hasPickupSchedule && (
          <>
            <Typography>{pickupLabel}</Typography>
            <Typography color="text-light" whiteSpace="nowrap">
              {pickupHours || fallbackLabel}
            </Typography>
          </>
        )}
        {hasDeliverySchedule && (
          <>
            <Typography>{deliveryLabel}</Typography>
            <Typography color="text-light" whiteSpace="nowrap">
              {deliveryHours || fallbackLabel}
            </Typography>
          </>
        )}
        {hasCurbsideSchedule && (
          <>
            <Typography>{curbsideLabel}</Typography>
            <Typography color="text-light" whiteSpace="nowrap">
              {curbsideHours || fallbackLabel}
            </Typography>
          </>
        )}
      </StyledGrid>
      <Link
        onClick={() => {
          showSchedule ? setShowSchedule(false) : setShowSchedule(true);
        }}
      >
        {showSchedule ? 'Hide' : 'View'} schedule
      </Link>
      <Hide isHidden={!showSchedule}>
        <MenuSchedule />
      </Hide>
    </Box>
  );
};
